"use client";

import { withSearchParams } from "@trydig/ui/next";
import { addLeadingSlash } from "@trydig/utils";
import type React from "react";
import { useState } from "react";
import type { ISbStoryData } from "storyblok-js-client";
import { sizes } from "@/components/bloks/Asset";
import { Button } from "@/components/ui/Button";
import { GridList } from "@/components/ui/GridList";
import { LoadMoreButton } from "@/components/ui/InfiniteView/components/LoadMoreButton";
import { Sentinel } from "@/components/ui/InfiniteView/components/Sentinel";
import { Skeleton } from "@/components/ui/Skeleton";
import { PAGE_SIZE } from "@/constants";
import { useTranslations } from "@/i18n/client";
import { usePathname } from "@/i18n/routing";
import { ViewItemList } from "@/lib/gtm/components";
import { sbLink } from "@/lib/storyblok/link";
import { getBaseUrl } from "@/lib/utils";
import type { BlokProductListingPage } from "@/storyblok";
import { ProductCard, ProductCardSkeleton } from "../ProductCard";
import { useProducts } from "./ProductListingContext";
import styles from "./ProductListingPage.module.css";

const ProductListHeader = withSearchParams<{
	showRootLink?: boolean;
}>(({ searchParams, showRootLink }) => {
	const { filterLinks, slug } = useProducts();

	const hasActiveLinkBySlug = filterLinks?.some(
		(link) => "href" in link && link.href === addLeadingSlash(slug),
	);

	const hasNoLinks = filterLinks?.length === 0;

	if (hasNoLinks) return null;

	return (
		<div className={styles.filterTabs}>
			{filterLinks?.map((link, i) => {
				if ((link.root && !showRootLink) || (link.root && hasActiveLinkBySlug))
					return null;

				const centraLinkActive =
					"centraCategory" in link &&
					searchParams.get("categories") === link.centraCategory;

				const linkActive =
					"href" in link && link.href === addLeadingSlash(slug);

				const rootActive =
					"root" in link && link.root && !searchParams.get("categories");

				return (
					<Button
						key={i}
						selected={centraLinkActive || rootActive || linkActive}
						onClick={() => {
							if ("centraCategory" in link) {
								searchParams.set("categories", link.centraCategory);
							}
							if ("root" in link && link.root) {
								searchParams.delete("categories");
							}
						}}
						variant="filter"
						href={"href" in link ? sbLink(link.href)?.href : undefined}
					>
						{link.text}
					</Button>
				);
			})}
		</div>
	);
});

const ProductListHeaderSkeleton = ({ size = 3 }: { size?: number }) => {
	return (
		<div className={styles.filterTabs}>
			{Array.from({ length: size })
				.fill(null)
				?.map((_, i) => {
					return (
						<Skeleton key={i} fitContent>
							<Button variant="filter">lorem</Button>
						</Skeleton>
					);
				})}
		</div>
	);
};

export const InfiniteProductList = ({
	content,
	story,
}: {
	content?: React.JSX.Element[];
	story?: ISbStoryData<BlokProductListingPage>;
}) => {
	const t = useTranslations();
	const [isAutoLoadEnabled, setIsAutoLoadEnabled] = useState<boolean>(false);
	const pathname = usePathname();
	const { query, firstPage, slug } = useProducts();
	const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = query;

	return (
		<div className={styles.root}>
			{firstPage && story?.content.centraCategory && (
				<ViewItemList
					products={firstPage.products}
					categoryPage={pathname}
					categoryId={`${getBaseUrl()}${pathname}`}
				/>
			)}
			<GridList
				header={<ProductListHeader showRootLink={story?.content.showAllLink} />}
				content={content}
				className={styles.gridList}
			>
				{data.pages.flatMap((page, i) =>
					page.products.map((product, j) => (
						<ProductCard
							priority={i === 0 && j <= 3}
							key={product.displayItemId}
							product={product}
							sizes={sizes(50, 25)}
							slug={slug}
						/>
					)),
				)}

				{isFetchingNextPage
					? [...Array(PAGE_SIZE)].map((_, idx) => (
							<ProductCardSkeleton key={idx} />
						))
					: null}
			</GridList>

			{isAutoLoadEnabled && hasNextPage ? (
				<Sentinel action={fetchNextPage} />
			) : (
				hasNextPage &&
				!isAutoLoadEnabled && (
					<LoadMoreButton
						action={() => {
							fetchNextPage();
							setIsAutoLoadEnabled(true);
						}}
						text={t("Load more")}
					/>
				)
			)}
		</div>
	);
};

export const InfiniteProductListSkeleton = ({
	size = PAGE_SIZE,
}: {
	size?: number;
}) => {
	return (
		<GridList
			content={[]}
			header={<ProductListHeaderSkeleton />}
			className={styles.gridList}
		>
			{[...Array(size)].map((_, idx) => (
				<ProductCardSkeleton key={idx} />
			))}
		</GridList>
	);
};
